import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, EyeOff, RotateCcw, ShieldAlert, ShieldCheck, UserCheck } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useDuplicateFindingGroup, useFinding, useFindingStatusHistory, useClientModules } from '../../hooks/useClientData';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { formatApiError, useApiClient } from '../../api/apiClient';
import { useAllFindings } from '../../context/FindingsContext';
import { useViewMode } from '../../context/ViewModeContext';
import { normalizeSeverity } from '../../utils/severity';
import { targetLabel, affectedLocations, cweIdOf, duplicateGroupKeyOf, locationOf, parseSddAws, parseSddCloud, parseSddFileShare, sddResourcePath, sddCategory } from '../../utils/findingTarget';
import { findingBackTarget } from '../../utils/findingNavigation';
import { isNessusVulnerabilityFinding, isSddFinding, isShodanVulnerabilityFinding } from '../../utils/findingClassification';
import { restoreFinding, restoreFindingErrorMessage } from '../../utils/findingRestore';
import { statusHistoryTimeline } from '../../utils/findingStatusHistory';
import { getRemediationText } from '../../utils/remediationText';
import { passiveEvidenceBadges } from '../../utils/passiveEvidence';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import SeverityBadge from '../../components/badges/SeverityBadge';
import StatusBadge from '../../components/badges/StatusBadge';
import KevBadge from '../../components/badges/KevBadge';
import Skeleton from '../../components/ui/Skeleton';
import FindingCorrectionEditor, { canCorrectFindings } from '../../components/findings/FindingCorrectionEditor';

const FINDING_STATUS = {
  OPEN: 0,
  REMEDIATION_CLAIMED: 1,
  VERIFIED: 2,
  ACCEPTED_RISK: 3,
  REOPENED: 4,
  POSSIBLY_REMEDIATED: 5,
};

const STATUS_ACTION_COPY = {
  [FINDING_STATUS.OPEN]: {
    title: 'Move to Open',
    prompt: 'Add an optional note about why this finding is being moved back to open:',
    placeholder: 'Optional note...',
    confirm: 'Move to Open',
  },
  [FINDING_STATUS.REOPENED]: {
    title: 'Reopen Finding',
    prompt: 'Add an optional note about why this finding is being reopened:',
    placeholder: 'Optional note...',
    confirm: 'Reopen',
  },
  [FINDING_STATUS.REMEDIATION_CLAIMED]: {
    title: 'Claim Remediation',
    prompt: 'Add an optional ownership or ticket note:',
    placeholder: 'e.g., Claimed by infrastructure team, ticket PSA-1234',
    confirm: 'Claim Remediation',
  },
  [FINDING_STATUS.VERIFIED]: {
    title: 'Mark Resolved / Verified',
    prompt: 'Add an optional note about the remediation or validation evidence:',
    placeholder: 'e.g., Patch applied and clean re-scan attached',
    confirm: 'Mark Verified',
  },
  [FINDING_STATUS.ACCEPTED_RISK]: {
    title: 'Accept Risk',
    prompt: 'Provide justification for accepting this risk. Re-detection will stay suppressed for 365 days from acceptance.',
    placeholder: 'e.g., DNS enumeration is expected for public domain, no remediation required',
    confirm: 'Accept Risk',
  },
};

const LIFECYCLE_STEPS = [
  { key: 'open', label: 'Open / Reopened', states: ['open', 'reopened'], color: C.red },
  { key: 'claimed', label: 'Claimed', states: ['remediation_claimed'], color: C.amb },
  { key: 'possibly_remediated', label: 'Possibly Remediated', states: ['possibly_remediated'], color: C.grn },
  { key: 'verified', label: 'Verified', states: ['verified'], color: C.grn },
  { key: 'accepted', label: 'Risk Accepted', states: ['accepted_risk'], color: C.t2 },
  { key: 'hidden', label: 'Hidden', states: ['suppressed'], color: C.amb },
];

function displayText(value, fallback = '\u2014') {
  if (value === undefined || value === null || value === '') return fallback;
  if (Array.isArray(value)) return value.filter(Boolean).map(v => displayText(v, '')).filter(Boolean).join('; ') || fallback;
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return fallback; }
  }
  return String(value);
}

function AffectedLocations({ paths, hosts, total }) {
  const [expanded, setExpanded] = useState(false);
  const DEFAULT_LIMIT = 5;
  const renderList = (title, items) => {
    if (!items || items.length === 0) return null;
    const shown = expanded ? items : items.slice(0, DEFAULT_LIMIT);
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>{title} ({items.length})</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {shown.map((x, i) => (
            <div key={`${title}-${i}`} style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, padding: '6px 10px', background: C.s2, border: `1px solid ${C.b2}`, borderRadius: 4, wordBreak: 'break-all' }}>{displayText(x)}</div>
          ))}
        </div>
      </div>
    );
  };
  return (
    <div style={{ marginTop: 18, padding: 16, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: C.t1, fontFamily: FONTS.sans }}>Affected Assets & Paths ({total})</div>
        {total > DEFAULT_LIMIT && (
          <button onClick={() => setExpanded(v => !v)} style={{ background: 'none', border: 'none', color: C.sky, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono, padding: 0 }}>
            {expanded ? 'Show less' : `Show all ${total}`}
          </button>
        )}
      </div>
      {renderList('Paths', paths)}
      {renderList('Hosts', hosts)}
    </div>
  );
}

function additionalDataOf(finding) {
  const raw = finding?.detail?.additionalData
    ?? finding?.detail?.AdditionalData
    ?? finding?.additionalData
    ?? finding?.AdditionalData
    ?? {};
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
}

function PassiveEvidenceBadges({ finding }) {
  const colorForTone = tone => {
    if (tone === 'confirmed') return C.grn;
    if (tone === 'source') return C.sky;
    if (tone === 'caution') return C.amb;
    return C.t2;
  };
  return passiveEvidenceBadges(finding).map(badge => (
    <Tag key={badge.key} label={badge.label} color={colorForTone(badge.tone)} />
  ));
}

function narrativeRows(finding) {
  const ad = additionalDataOf(finding);
  return [
    ['Action Summary', ad.narrativeActionSummary],
    ['Business Impact', ad.narrativeBusinessImpact],
    ['Technical Context', ad.narrativeTechnicalContext],
    ['Evidence Checklist', ad.narrativeEvidenceChecklist],
    ['Validation Steps', ad.narrativeValidationSteps],
    ['Exit Criteria', ad.narrativeRemediationExitCriteria],
    ['Recommended Owner', ad.narrativeRecommendedOwner],
    ['Target SLA', ad.narrativeRemediationSla],
    ['Remediation Leverage', ad.narrativeRiskReduction],
    ['Estimated Effort', ad.narrativeEffort],
    ['Impact Band', ad.narrativeBreachCostAvoided],
    ['Client Conversation', ad.narrativeClientConversation],
  ].filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
}

function narrativeValue(finding, key, fallback = '') {
  const ad = additionalDataOf(finding);
  const value = ad?.[key] ?? ad?.[key.charAt(0).toUpperCase() + key.slice(1)];
  return value !== undefined && value !== null && String(value).trim() !== '' ? String(value) : fallback;
}

function splitGuidance(value, fallback = []) {
  const text = displayText(value, '').replace(/\r/g, '').trim();
  if (!text) return fallback;
  const numbered = text
    .split(/(?=(?:^|\n|\s)\d+[\).]\s+)/g)
    .map(x => x.replace(/^\s*\d+[\).]\s*/, '').trim())
    .filter(Boolean);
  if (numbered.length > 1) return numbered;
  const lines = text
    .split(/\n+/)
    .map(x => x.replace(/^\s*[-*]\s*/, '').replace(/^\s*\d+[\).]\s*/, '').trim())
    .filter(Boolean);
  if (lines.length > 1) return lines;
  return [text];
}

function compliancePills(finding, limit = 6) {
  const mappings = Array.isArray(finding?.complianceMappings) ? finding.complianceMappings : [];
  return mappings.map(m => {
    if (typeof m === 'string') return m;
    if (!m || typeof m !== 'object') return '';
    const framework = frameworkDisplayName(m.frameworkName ?? m.frameworkId ?? m.framework ?? '');
    const control = m.controlId ?? m.control ?? '';
    return [framework, control].filter(Boolean).join(' ');
  }).filter(Boolean).slice(0, limit);
}

function frameworkDisplayName(value) {
  const raw = String(value || '').trim();
  const key = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (key.includes('cmmc1') || key.includes('level1')) return 'CMMC Level 1 v2.0';
  if (key.includes('cmmc2') || key.includes('level2')) return 'CMMC Level 2 v2.0';
  if (key.includes('cmmc')) return 'CMMC v2.0';
  if (key.includes('nist800171')) return 'NIST 800-171 R2';
  if (key.includes('nist80053')) return 'NIST 800-53 Rev. 5';
  if (key.includes('nistcsf')) return 'NIST CSF 2.0';
  if (key === 'nist' || key.includes('nist')) return 'NIST 800-53 Rev. 5';
  if (key.includes('cis')) return 'CIS Controls v8.1';
  if (key.includes('soc2') || key === 'soc') return 'SOC 2 Type II';
  if (key.includes('hipaa')) return 'HIPAA Security Rule';
  if (key.includes('pci')) return 'PCI DSS v4.0.1';
  if (key.includes('iso')) return 'ISO/IEC 27001:2022';
  return raw || 'Other';
}

function copyToClipboard(text, onCopied) {
  navigator.clipboard.writeText(text).then(() => {
    onCopied();
  });
}

function remediationTextOf(finding) {
  const ad = additionalDataOf(finding);
  return finding?.remediationGuidance
    || finding?.detail?.remediationGuidance
    || finding?.detail?.solution
    || finding?.detail?.remediation
    || ad?.solution
    || ad?.remediation
    || finding?.remediation
    || getRemediationText(finding?.module, finding?.title, ad)
    || '';
}

function fallbackValidationSteps(finding) {
  const module = String(finding?.module || finding?.sourceModule || '').toLowerCase();
  if (module.startsWith('sdd-')) {
    return [
      'Confirm the detected file, object, or record belongs to this client.',
      'Validate the sensitivity category and whether the content is still present.',
      'Review file permissions and sharing scope to determine who can access it.',
      'Re-run SDD after remediation and retain before/after evidence.',
    ];
  }
  if (module.startsWith('ad-')) {
    return [
      'Identify the affected AD object, group, ACL, trust, session, or certificate template.',
      'Confirm whether the relationship is required for business operations.',
      'Remove or constrain the risky permission path.',
      'Re-run the AD module and confirm the same path no longer appears.',
    ];
  }
  if (module.startsWith('dast-')) {
    return [
      'Confirm the affected URL, method, parameter, and proof from the scanner.',
      'Validate the application fix in an approved environment or maintenance window.',
      'Re-run the same DAST test against the original target.',
      'Check adjacent routes or parameters for the same weakness.',
    ];
  }
  return [
    'Confirm the finding belongs to the selected client and asset.',
    'Determine whether the asset, identity, or service is still active.',
    'Apply the remediation or compensating control.',
    'Validate with the source system and document residual risk.',
  ];
}

function isDastFinding(finding) {
  const source = [
    finding?.module,
    finding?.sourceModule,
    finding?.fingerprint,
    finding?.scanner,
    finding?.source,
  ].filter(Boolean).join(' ').toLowerCase();
  return source.includes('dast') || source.includes('zap') || source.includes('nuclei');
}

function findingFamilyOf(finding) {
  const source = [
    finding?.module,
    finding?.sourceModule,
    finding?.fingerprint,
    finding?.scanner,
    finding?.source,
    finding?.title,
    finding?.category,
  ].filter(Boolean).join(' ').toLowerCase();

  if (isDastFinding(finding)) return 'dast';
  if (isSddFinding(finding)) return 'sensitiveData';
  if (source.includes('ad-') || source.includes('active directory') || source.includes('entra') || source.includes('domain controller')) return 'ad';
  if (source.includes('cloud') || source.includes('aws') || source.includes('azure') || source.includes('m365') || source.includes('cspm')) return 'cloud';
  if (source.includes('osint') || source.includes('shodan') || source.includes('breach') || source.includes('certificate') || source.includes('exposed service')) return isShodanVulnerabilityFinding(finding) ? 'vulnerability' : 'osint';
  if (isNessusVulnerabilityFinding(finding) || isShodanVulnerabilityFinding(finding) || finding?.cve || finding?.cveId) return 'vulnerability';
  return 'general';
}

const FAMILY_GUIDANCE = {
  vulnerability: {
    label: 'Vulnerability',
    owner: 'Infrastructure / security engineering',
    validation: [
      'Confirm the affected host, port, service, and scanner evidence match the client asset inventory.',
      'Apply the vendor patch, configuration change, or compensating control appropriate for the confirmed service.',
      'Re-run the same vulnerability check against the original target.',
      'Attach before/after scan evidence, patch record, and any exception notes to the ticket.',
    ],
    evidence: 'Affected host and port, detected service/version, CVE or plugin evidence, remediation record, clean re-scan output, and residual risk decision if the issue remains accepted.',
    exit: 'The CVE or plugin no longer reproduces on the original host and service, or an approved compensating control and exception record is attached.',
    business: 'This vulnerability creates a path for exploitation until the affected service is patched, reconfigured, or formally risk-accepted with evidence.',
    action: 'Assign the service owner, confirm applicability, remediate the affected service, and re-scan to prove closure.',
    talk: 'We found a service vulnerability that should be handled through the normal remediation queue. The value is not just patching it, but proving with a clean re-scan that the exposure is gone.',
    technical: 'Technical validation should include the plugin or CVE evidence, detected product, target service, exploitability context such as KEV or EPSS when available, and the re-test artifact.',
  },
  cloud: {
    label: 'Cloud Security',
    owner: 'Cloud platform owner',
    validation: [
      'Confirm the affected account, subscription, tenant, region, and resource scope.',
      'Apply the recommended baseline or policy change through IaC, CLI, console, or the approved cloud control plane.',
      'Check every affected region or resource instance included in the rollup.',
      'Re-run cloud posture collection and attach the updated control state.',
    ],
    evidence: 'Cloud account or subscription, affected regions/resources, current control state, remediation command or change record, policy evidence, and post-change scan result.',
    exit: 'The cloud control reports compliant across the affected scope, or a documented exception lists every region/resource still intentionally excluded.',
    business: 'This cloud control gap increases exposure across shared infrastructure and may affect multiple workloads until the baseline is restored.',
    action: 'Have the cloud owner remediate the control at policy or account scope, then verify the finding across every affected region or resource.',
    talk: 'This is a cloud posture issue, so the best remediation is usually a tenant, account, or policy-level change instead of one-off cleanup. We will validate the full affected scope after the change.',
    technical: 'Cloud validation should identify provider, account/subscription, region, resource identifier, failed control, and the exact post-remediation posture result.',
  },
  ad: {
    label: 'Active Directory',
    owner: 'Identity / Active Directory owner',
    validation: [
      'Identify the affected AD object, group, ACL, trust, session, or certificate template.',
      'Confirm whether the relationship or permission is required for business operations.',
      'Remove, constrain, or monitor the risky permission path.',
      'Re-run the AD module and confirm the same path no longer appears.',
    ],
    evidence: 'Affected identity object, group or ACL path, current privilege relationship, approved change, re-run AD evidence, and any compensating monitoring control.',
    exit: 'The risky identity relationship is removed or constrained, and the AD assessment no longer reports the same attack path or privilege exposure.',
    business: 'This identity weakness can help an attacker move from a normal foothold toward privileged access if it remains unresolved.',
    action: 'Review the identity relationship with the AD owner, remove unnecessary privilege, and re-run AD validation.',
    talk: 'This is an identity control issue. The remediation goal is to reduce how far an attacker could move if one account or workstation were compromised.',
    technical: 'AD validation should include object distinguished names, group membership or ACL path, template/trust/session evidence, and the re-run result.',
  },
  dast: {
    label: 'DAST',
    owner: 'Application owner / web engineering team',
    validation: [
      'Confirm the affected URL, method, parameter, and proof from the scanner.',
      'Validate the application fix in an approved environment or maintenance window.',
      'Re-run the same DAST test against the original target.',
      'Check adjacent routes or parameters for the same weakness.',
    ],
    evidence: 'Affected URL, HTTP method, vulnerable parameter or template, scanner proof, request/response evidence, application fix reference, and clean re-scan result.',
    exit: 'The same DAST check no longer reproduces against the original target, adjacent routes are reviewed for the same weakness, and the re-scan artifact is attached.',
    business: 'This application weakness may expose customer-facing workflows, authenticated sessions, or public web services until the affected route is fixed and re-tested.',
    action: 'Assign the affected application owner, fix the vulnerable route or configuration, then re-run the same DAST check to prove closure.',
    talk: 'The DAST scan reproduced an application security issue on the affected target. The next step is to fix the vulnerable behavior and re-test the same route so we can show clean validation evidence.',
    technical: 'DAST evidence should include the target URL, module or template, request context, proof output, and the re-test result after remediation.',
  },
  sensitiveData: {
    label: 'Sensitive Data',
    owner: 'Data owner / collaboration platform owner',
    validation: [
      'Confirm the detected file, object, or record belongs to this client.',
      'Validate the sensitivity category and whether the content is still present.',
      'Review permissions, sharing links, and external recipients.',
      'Remove unnecessary exposure, relocate or protect the data, then re-run SDD validation.',
    ],
    evidence: 'File or object path, sensitivity category, permission summary, sharing state, remediation action, owner approval, and post-remediation SDD result.',
    exit: 'Sensitive content is removed, protected, or access-restricted, and the re-scan confirms the same exposure is no longer present.',
    business: 'This finding identifies sensitive information in a location or sharing state that may create privacy, contractual, or compliance exposure.',
    action: 'Confirm the data owner, validate access scope, remove unnecessary sharing, and re-scan the location.',
    talk: 'This is a data exposure issue. The priority is confirming who owns the content, who can access it, and whether it should be protected, moved, or removed.',
    technical: 'SDD validation should include storage source, full path, sensitivity category, permission evidence, sharing links, and the post-remediation scan result.',
  },
  osint: {
    label: 'OSINT Exposure',
    owner: 'Security operations / external attack surface owner',
    validation: [
      'Confirm the exposed identity, domain, IP, certificate, or service belongs to the client.',
      'Determine whether the exposure is expected, stale, or unmanaged.',
      'Remove, rotate, restrict, or document the exposure through the owning system.',
      'Re-run external exposure collection and retain before/after evidence.',
    ],
    evidence: 'Observed external artifact, discovery source, ownership confirmation, remediation or exception decision, and post-change external validation.',
    exit: 'The exposed artifact is no longer externally visible, is rotated or restricted, or has an approved business exception with monitoring.',
    business: 'This public exposure shapes what attackers can discover about the client before they ever authenticate.',
    action: 'Validate ownership, decide whether the exposure is expected, and remediate or document it with external re-check evidence.',
    talk: 'This finding comes from the outside-in view. It helps show what an attacker can learn or target before interacting with internal systems.',
    technical: 'OSINT validation should include discovery source, observed value, first/last seen timestamps, ownership evidence, and the updated external scan result.',
  },
  general: {
    label: 'Security Finding',
    owner: 'Security / IT operations owner',
    validation: [
      'Confirm the finding belongs to the selected client and asset.',
      'Determine whether the asset, identity, or service is still active.',
      'Apply the remediation or compensating control.',
      'Validate with the source system and document residual risk.',
    ],
    evidence: 'Source, affected asset, discovery date, scanner evidence, remediation action, validation date, and residual risk decision.',
    exit: 'Validate the finding is no longer present in the source system and document the confirmation source and date.',
    business: 'This issue increases technical and business risk until ownership, remediation, and validation are complete.',
    action: 'Review and remediate this finding according to policy.',
    talk: 'Use this finding to align on business risk, ownership, and timing.',
    technical: 'Technical validation should include source evidence, affected target, remediation record, and closure proof.',
  },
};

function SectionLabel({ children, color = C.sky }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{ width: 4, height: 18, borderRadius: 999, background: color }} />
      <div style={{ fontSize: 10, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, fontWeight: 800 }}>{children}</div>
    </div>
  );
}

function StepList({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {steps.map((step, index) => (
        <div key={`${step}-${index}`} style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: 10, padding: '9px 0', borderBottom: index === steps.length - 1 ? 'none' : `1px solid ${C.b2}` }}>
          <span style={{ width: 22, height: 22, borderRadius: '50%', background: `${C.sky}cc`, color: C.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, fontFamily: FONTS.mono }}>{index + 1}</span>
          <span style={{ color: C.t1, fontSize: 12, lineHeight: 1.6 }}>{displayText(step)}</span>
        </div>
      ))}
    </div>
  );
}

function InsightCard({ title, children, accent = C.grn }) {
  return (
    <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 8, padding: 16, minWidth: 0 }}>
      <SectionLabel color={accent}>{title}</SectionLabel>
      {children}
    </div>
  );
}

function MetricTile({ label, value, highlight }) {
  return (
    <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 7, padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>{label}</div>
      <div style={{ color: highlight || C.t1, fontSize: 13, fontWeight: 900, fontFamily: FONTS.sans, lineHeight: 1.35, wordBreak: 'break-word' }}>{displayText(value)}</div>
    </div>
  );
}

function Breadcrumb({ items }) {
  return (
    <nav aria-label="Breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12, flexWrap: 'wrap' }}>
      {items.map((item, index) => (
        <span key={`${item.label}-${index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          {item.onClick ? (
            <button
              type="button"
              onClick={item.onClick}
              style={{ background: 'none', border: 'none', color: C.sky, cursor: 'pointer', padding: 0, fontSize: 11, fontFamily: FONTS.sans, fontWeight: 650 }}
            >
              {item.label}
            </button>
          ) : (
            <span style={{ color: C.t2, fontSize: 11, fontFamily: FONTS.sans, fontWeight: 650 }}>{item.label}</span>
          )}
          {index < items.length - 1 && <span style={{ color: C.t3, fontSize: 11 }}>{'\u203a'}</span>}
        </span>
      ))}
    </nav>
  );
}

function TextBlock({ children, mono = false }) {
  return <div style={{ fontSize: 12, color: C.t1, lineHeight: 1.7, fontFamily: mono ? FONTS.mono : FONTS.sans, whiteSpace: 'pre-wrap' }}>{children}</div>;
}

function referenceListOf(finding) {
  const refs = finding?.references ?? [];
  if (!Array.isArray(refs)) return [];
  const seen = new Set();
  return refs
    .map(ref => {
      if (typeof ref === 'string') return { title: ref, url: ref };
      const url = displayText(ref?.url ?? ref?.Url ?? '', '');
      const title = displayText(ref?.title ?? ref?.Title ?? url, url);
      return { title, url };
    })
    .filter(ref => ref.url && /^https?:\/\//i.test(ref.url))
    .filter(ref => {
      const key = `${displayText(ref.title, '')}|${displayText(ref.url, '')}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function listFromAd(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      } catch { /* fall through */ }
    }
    return trimmed.split(/[;\n]/).map(x => x.trim()).filter(Boolean);
  }
  return [];
}

function boolText(value) {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === 'true') return 'Yes';
  if (v === 'false') return 'No';
  return value ? String(value) : 'Unknown';
}

function formatAdDate(value, fallback = '\u2014') {
  if (!value) return fallback;
  const safe = displayText(value, '');
  const date = new Date(safe);
  if (Number.isNaN(date.getTime())) return safe || fallback;
  return exactTime(date);
}

function formatBytes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return displayText(value);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function SddAccessDetail({ finding }) {
  const ad = additionalDataOf(finding);
  const cloud = parseSddCloud(finding);
  const aws = parseSddAws(finding);
  const fileShare = parseSddFileShare(finding);
  const isSdd = cloud || aws || fileShare || (finding?.module || '').toLowerCase().startsWith('sdd-') || (finding?.fingerprint || '').toLowerCase().startsWith('sdd-');
  if (!isSdd) return null;

  const fileUrl = displayText(
    ad.webUrl || ad.WebUrl || ad.webURL || ad.url || ad.fileUrl || ad.FileUrl || ad.objectUrl || ad.ObjectUrl || '',
    ''
  );
  const fullPath = displayText(sddResourcePath(finding) || ad.filePath || ad.FilePath || ad.resourcePath || ad.ResourcePath || finding?.detail?.affectedComponent || finding?.asset || '', '');
  const category = displayText(sddCategory(finding) || ad.category || ad.patternCategory || '', '');
  const principals = listFromAd(ad.permissionPrincipalsJson || ad.permissionPrincipals);
  const externalRecipients = listFromAd(ad.sharingExternalRecipientsJson || ad.sharingExternalRecipients);
  const sharingFlags = [
    ['Anonymous link', ad.sharingAnonymous],
    ['Org-wide link', ad.sharingOrganizationWide],
    ['Specific people', ad.sharingSpecificPeople],
    ['External sharing', ad.sharingExternal],
  ];
  const detailRows = [
    ['Source', cloud?.source || aws?.source || fileShare?.source || ad.sourceLabel || finding.module],
    ['Category', category],
    ['Last modified', formatAdDate(ad.lastModifiedDateTime)],
    ['Last accessed', ad.lastAccessedDateTime ? formatAdDate(ad.lastAccessedDateTime) : (ad.lastAccessedDateTimeNote || 'Not exposed by Microsoft Graph driveItem metadata')],
    ['File size', formatBytes(ad.fileSizeBytes)],
    ['Permission scan', ad.permissionScanStatus || 'Not available on this finding yet'],
  ];

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 7 }}>Sensitive Data Location</div>
      <div style={{ background: `${C.sky}08`, border: `1px solid ${C.sky}22`, borderRadius: 7, padding: 14 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 4 }}>Full File Path</div>
          {fileUrl ? (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.sky, fontSize: 12, fontFamily: FONTS.mono, wordBreak: 'break-all', lineHeight: 1.55 }}>
              {displayText(fullPath || fileUrl)}
            </a>
          ) : (
            <div style={{ color: C.t1, fontSize: 12, fontFamily: FONTS.mono, wordBreak: 'break-all', lineHeight: 1.55 }}>{displayText(fullPath)}</div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, marginBottom: 12 }}>
          {detailRows.filter(([, value]) => value).map(([label, value]) => (
            <div key={label} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, lineHeight: 1.5, wordBreak: 'break-word' }}>{displayText(value)}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 11 }}>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>Permissions</div>
            <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.6, fontFamily: FONTS.sans, marginBottom: principals.length ? 8 : 0 }}>
              {displayText(ad.permissionSummary, 'No permission summary is available until this finding is regenerated by an enriched SDD scan.')}
            </div>
            {principals.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {principals.slice(0, 12).map(p => (
                  <div key={p} style={{ fontSize: 10, color: C.t1, fontFamily: FONTS.mono, wordBreak: 'break-all', padding: '5px 7px', background: C.s1, border: `1px solid ${C.b2}`, borderRadius: 4 }}>{p}</div>
                ))}
                {principals.length > 12 && <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>+{principals.length - 12} more principals</div>}
              </div>
            )}
          </div>

          <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 11 }}>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>Sharing</div>
            <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.6, fontFamily: FONTS.sans, marginBottom: 8 }}>
              {displayText(ad.sharingSummary, 'No sharing summary is available until this finding is regenerated by an enriched SDD scan.')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: externalRecipients.length ? 8 : 0 }}>
              {sharingFlags.map(([label, value]) => (
                <span key={label} style={{
                  fontSize: 9,
                  fontFamily: FONTS.mono,
                  color: String(value).toLowerCase() === 'true' ? C.red : C.t2,
                  background: String(value).toLowerCase() === 'true' ? C.redBg : C.s1,
                  border: `1px solid ${String(value).toLowerCase() === 'true' ? C.red + '44' : C.b2}`,
                  borderRadius: 999,
                  padding: '3px 7px',
                }}>
                  {label}: {boolText(value)}
                </span>
              ))}
            </div>
            {externalRecipients.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {externalRecipients.slice(0, 8).map(p => (
                  <div key={p} style={{ fontSize: 10, color: C.red, fontFamily: FONTS.mono, wordBreak: 'break-all' }}>{p}</div>
                ))}
              </div>
            )}
            {ad.sharingLinks && (
              <div style={{ marginTop: 8, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, wordBreak: 'break-all' }}>
                Links: {displayText(ad.sharingLinks)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DuplicateGroupSection({ groupKey, group, loading, error, currentFindingId, onOpen }) {
  if (!groupKey) return null;
  const members = Array.isArray(group?.members) ? group.members : [];
  const memberCount = group?.memberCount ?? members.length;

  return (
    <div style={{ marginTop: 18, padding: 16, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, color: C.t1, fontFamily: FONTS.sans }}>Duplicate Group Members</div>
          <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 3 }}>
            {loading ? 'Loading duplicate group...' : error ? 'Group details unavailable' : `${memberCount || 0} related finding${memberCount === 1 ? '' : 's'}`}
          </div>
        </div>
        <span style={{ fontSize: 9, color: C.amb, fontFamily: FONTS.mono, fontWeight: 800, padding: '3px 7px', borderRadius: 4, background: `${C.amb}12`, border: `1px solid ${C.amb}33`, wordBreak: 'break-all' }}>
          {displayText(groupKey)}
        </span>
      </div>
      {error && (
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
          {error}
        </div>
      )}
      {!loading && !error && group?.unavailable && (
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
          This backend does not have duplicate group details for this key yet.
        </div>
      )}
      {!loading && !error && members.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.map(member => {
            const isCurrent = member.id === currentFindingId;
            const loc = locationOf(member) || member.asset || 'No affected location';
            return (
              <button
                key={member.id || `${member.title}-${loc}`}
                type="button"
                onClick={() => member.id && onOpen(member)}
                disabled={!member.id}
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  gap: 8,
                  alignItems: 'center',
                  padding: '8px 10px',
                  borderRadius: 5,
                  border: `1px solid ${isCurrent ? C.sky : C.b2}`,
                  background: isCurrent ? `${C.sky}10` : C.s2,
                  color: C.t1,
                  cursor: member.id ? 'pointer' : 'default',
                  textAlign: 'left',
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 11, color: C.t1, fontFamily: FONTS.mono, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayText(member.title, 'Untitled finding')}{isCurrent ? ' (current)' : ''}
                  </span>
                  <span style={{ display: 'block', fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayText(loc)}{member.age ? ` / ${relativeTime(member.age)}` : ''}
                  </span>
                </span>
                <SeverityBadge value={member.sev} />
                <StatusBadge value={member.status} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FindingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useClient();
  const { isPlatformAdmin, isMspUser, profile } = useUser();
  const canWrite = isPlatformAdmin || isMspUser;
  const api = useApiClient();
  const { data: f, loading, error, refetch: refetchFinding } = useFinding(id, location.state?.finding);
  const { data: statusHistory, loading: statusHistoryLoading, error: statusHistoryError, refetch: refetchStatusHistory } = useFindingStatusHistory(id);
  const { removeFinding, refetch: refetchAllFindings } = useAllFindings();
  const { isExecutive } = useViewMode();

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [showAllCompliance, setShowAllCompliance] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [idCopied, setIdCopied] = useState(false);
  const [copyToast, setCopyToast] = useState('');
  const [plainEnglish, setPlainEnglish] = useState(false);
  const [showHideModal, setShowHideModal] = useState(false);
  const [hideReason, setHideReason] = useState('');
  const [hiding, setHiding] = useState(false);
  const [hideError, setHideError] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');

  // PSA ticket creation
  const [psaConfigured, setPsaConfigured] = useState(null); // null=loading, true/false
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [ticketResult, setTicketResult] = useState(null);
  const [ticketError, setTicketError] = useState('');

  // Credential validation
  const [showPwd, setShowPwd] = useState(false);
  const [hashCopied, setHashCopied] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [validateProvider, setValidateProvider] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const [validateError, setValidateError] = useState('');
  const [liveSev, setLiveSev] = useState(null);
  const { m365, ad: adEnabled, loading: modulesLoading } = useClientModules();
  const backTarget = findingBackTarget(f, location.state);
  const goBackToSource = () => navigate(backTarget.path);
  const duplicateGroupKey = duplicateGroupKeyOf(f);
  const { data: duplicateGroup, loading: duplicateGroupLoading, error: duplicateGroupError } = useDuplicateFindingGroup(duplicateGroupKey);
  const openDuplicateMember = (member) => navigate(`/client/findings/${member.id}`, {
    state: { fromPath: backTarget.path, fromLabel: backTarget.label },
  });

  const complianceControlUrl = (framework, label) => {
    const fw = String(framework || '').toLowerCase();
    const text = String(label || '');
    if (!fw.includes('nist')) return null;
    const match = text.match(/\b([A-Z]{2})[-\s]?(\d+(?:\.\d+)?)\b/i);
    if (!match) return 'https://csrc.nist.gov/projects/risk-management';
    const control = `${match[1].toUpperCase()}-${match[2]}`;
    return `https://csrc.nist.gov/projects/risk-management/sp800-53-controls/release-search#!/control?version=5.1&number=${encodeURIComponent(control)}`;
  };

  useEffect(() => {
    if (!canWrite) return;
    api.get('/msp/integrations/psa/config')
      .then(d => setPsaConfigured(d?.configured === true))
      .catch(() => setPsaConfigured(false));
  }, [canWrite]);

  const handleHideFinding = async () => {
    if (!hideReason.trim()) return;
    setHiding(true);
    setHideError('');
    try {
      await api.post(`/clients/${clientId}/findings/${id}/suppress`, { reason: hideReason });
      removeFinding(id);
      goBackToSource();
    } catch (err) {
      setHideError(err.message);
    } finally {
      setHiding(false);
    }
  };

  const handleRestoreFinding = async () => {
    setRestoring(true);
    setRestoreError('');
    try {
      await restoreFinding(api, clientId, id);
      window.location.reload();
    } catch (err) {
      setRestoreError(restoreFindingErrorMessage(err));
    } finally {
      setRestoring(false);
    }
  };

  const copyFindingId = () => {
    copyToClipboard(f?.id || id, () => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    });
  };

  const showCopied = (message) => {
    setCopyToast(message);
    setTimeout(() => setCopyToast(''), 2200);
  };

  const handleCreateTicket = async () => {
    setCreatingTicket(true);
    setTicketError('');
    setTicketResult(null);
    try {
      const result = await api.post('/msp/integrations/psa/tickets', {
        findingId: f?.id || id,
        findingUrl: window.location.href,
      });
      setTicketResult(result);
    } catch (err) {
      setTicketError(err.message);
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleValidateCredential = async () => {
    setValidating(true);
    setValidateError('');
    setValidateResult(null);
    try {
      const result = await api.post(`/clients/${clientId}/findings/${id}/validate-credential`, {
        provider: validateProvider,
      });
      setValidateResult(result);
      if (result.severity !== undefined) setLiveSev(normalizeSeverity(result.severity).toLowerCase());
      setShowValidateModal(false);
    } catch (err) {
      setValidateError(err.message);
      setShowValidateModal(false);
    } finally {
      setValidating(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdatingStatus(true);
    setStatusError('');
    try {
      await api.patch(`/clients/${clientId}/findings/${id}/status`, {
        status: newStatus,
        note: statusNote,
      });
      await Promise.all([refetchFinding(), refetchStatusHistory()]);
      setStatusNote('');
      setShowStatusModal(false);
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openModal = (status) => {
    setTargetStatus(status);
    setStatusNote('');
    setStatusError('');
    setShowStatusModal(true);
  };

  if (loading) return (
    <Shell title="Loading...">
      <div style={{ marginBottom: 12 }}><Button variant="ghost" onClick={goBackToSource}>{'\u2190'} Back to {backTarget.label}</Button></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <Card style={{ padding: 22 }}>
          <Skeleton width="60%" height={20} style={{ marginBottom: 12 }} />
          <Skeleton width="40%" height={14} style={{ marginBottom: 20 }} />
          <Skeleton height={80} style={{ marginBottom: 14 }} />
          <Skeleton height={120} />
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card style={{ padding: 14 }}><Skeleton height={120} /></Card>
          <Card style={{ padding: 14 }}><Skeleton height={140} /></Card>
        </div>
      </div>
    </Shell>
  );

  if (!f || error) return (
    <Shell title="Not Found">
      <div style={{ padding: 40, textAlign: 'center', color: C.t2 }}>
        {error ? `Error: ${formatApiError(error)}` : `Finding ${id} not found.`}{' '}
        <button onClick={goBackToSource} style={{ color: C.sky, background: 'none', border: 'none', cursor: 'pointer' }}>{'\u2190'} Back</button>
      </div>
    </Shell>
  );

  const age = f.age ? new Date(f.age) : null;
  const statusChangedAt = f.statusChangedAt ? new Date(f.statusChangedAt) : null;
  const statusChangedBy = f.statusChangedBy || null;
  const fallbackTimeline = [
    { l: 'Finding detected', t: age, dot: C.t2 },
    ...(f.status === 'remediation_claimed' ? [{ l: `Remediation claimed${statusChangedBy ? ` by ${statusChangedBy}` : ''}`, t: statusChangedAt, dot: C.amb }] : []),
    ...(f.status === 'verified' ? [{ l: `Remediation verified${statusChangedBy ? ` by ${statusChangedBy}` : ''} \u2014 finding closed`, t: statusChangedAt, dot: C.grn }] : []),
    ...(f.status === 'accepted_risk' ? [{ l: `Risk accepted${statusChangedBy ? ` by ${statusChangedBy}` : ''}`, t: statusChangedAt, dot: C.gry }] : []),
    ...(f.status === 'reopened' ? [{ l: `Reopened${statusChangedBy ? ` by ${statusChangedBy}` : ''}`, t: statusChangedAt, dot: C.red }] : []),
    ...(f.status === 'possibly_remediated' ? [{ l: `Possibly remediated${statusChangedBy ? ` by ${statusChangedBy}` : ''} after latest scan`, t: statusChangedAt, dot: C.grn }] : []),
  ];
  const timeline = statusHistory?.length > 0 ? statusHistoryTimeline(statusHistory, f, C) : fallbackTimeline;

  const isOpen = f.status === 'open' || f.status === 'reopened';
  const isSuppressed = f.status === 'suppressed' || f.suppressed === true || f.isSuppressedInPortal === true;
  const canMarkResolved = isOpen || f.status === 'remediation_claimed';
  const ad = additionalDataOf(f);
  const moduleName = displayText(f.module || f.sourceModule || f.fingerprint?.split(/[-:]/)[0] || 'finding');
  const findingFamily = findingFamilyOf(f);
  const familyGuidance = FAMILY_GUIDANCE[findingFamily] || FAMILY_GUIDANCE.general;
  const isDast = findingFamily === 'dast';
  const remediationText = remediationTextOf(f);
  const remediationSteps = splitGuidance(remediationText);
  const validationSteps = splitGuidance(narrativeValue(f, 'narrativeValidationSteps'), familyGuidance.validation);
  const evidenceChecklist = narrativeValue(f, 'narrativeEvidenceChecklist', familyGuidance.evidence);
  const exitCriteria = narrativeValue(f, 'narrativeRemediationExitCriteria', familyGuidance.exit);
  const businessImpact = narrativeValue(f, 'narrativeBusinessImpact', familyGuidance.business);
  const actionSummary = narrativeValue(f, 'narrativeActionSummary', remediationSteps[0] || remediationText || familyGuidance.action);
  const clientConversation = narrativeValue(f, 'narrativeClientConversation', `${familyGuidance.talk} Start with: ${actionSummary}`);
  const recommendedOwner = narrativeValue(f, 'narrativeRecommendedOwner', familyGuidance.owner);
  const remediationSla = narrativeValue(f, 'narrativeRemediationSla', normalizeSeverity(f.sev) === 'Critical' ? 'Begin within 24h / validate within 7d' : 'Schedule into the agreed remediation window');
  const effort = narrativeValue(f, 'narrativeEffort', normalizeSeverity(f.sev) === 'Critical' || normalizeSeverity(f.sev) === 'High' ? '2-8 hours' : '1-4 hours');
  const impactBand = narrativeValue(f, 'narrativeBreachCostAvoided', normalizeSeverity(f.sev) === 'Critical' ? 'Material - priority remediation' : 'Significant - active remediation cycle');
  const remediationLeverage = narrativeValue(f, 'narrativeRiskReduction', normalizeSeverity(f.sev) === 'Critical' ? 'High leverage - removes priority exposure' : 'Moderate leverage - reduces practical exposure');
  const technicalContext = narrativeValue(f, 'narrativeTechnicalContext', familyGuidance.technical);
  const affected = affectedLocations(f);
  const refs = referenceListOf(f);
  const cwe = cweIdOf(f);
  const topCompliancePills = compliancePills(f);
  const clientEmailText = `Hi team,\n\nWe identified a ${normalizeSeverity(f.sev).toLowerCase()} security finding: "${displayText(f.title, 'Finding')}" on ${targetLabel(f) || affected.hosts[0] || affected.paths[0] || 'the affected environment'}. ${clientConversation}\n\nRecommended next step: ${actionSummary}\n\nBusiness impact: ${businessImpact} Impact band: ${impactBand}.`;
  const ticketText = [
    `Summary: ${displayText(f.title, 'Finding')}`,
    `Severity: ${normalizeSeverity(f.sev)}`,
    `Status: ${displayText(f.status)}`,
    `Asset/Target: ${targetLabel(f) || affected.hosts[0] || affected.paths[0] || 'Unassigned'}`,
    `Source: ${moduleName}`,
    f.cve ? `CVE: ${f.cve}` : null,
    f.ep ? `EPSS: ${Math.round(f.ep * 100)}%` : null,
    '',
    'Business impact:',
    businessImpact,
    '',
    'Remediation:',
    remediationSteps.map((step, i) => `${i + 1}. ${step}`).join('\n'),
    '',
    'Validation steps:',
    validationSteps.map((step, i) => `${i + 1}. ${step}`).join('\n'),
    '',
    `Evidence checklist: ${evidenceChecklist}`,
    `Exit criteria: ${exitCriteria}`,
    `Finding URL: ${window.location.href}`,
  ].filter(x => x !== null).join('\n');

  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/client/dashboard') },
    { label: backTarget.label || `${familyGuidance.label} Findings`, onClick: goBackToSource },
    { label: displayText(f.id || f.cve || id, 'Finding') },
  ];
  const heroMetrics = isExecutive
    ? [
        { l: 'Business Impact', v: businessImpact, c: C.grn },
        { l: 'Impact Band', v: impactBand, c: C.grn },
        { l: 'Owner', v: recommendedOwner, c: C.t1 },
        { l: 'SLA', v: remediationSla, c: C.amb },
      ]
    : [
        { l: 'CVSS', v: f.cvss ? displayText(f.cvss) : '-', c: Number(f.cvss) >= 9 ? C.red : C.t1 },
        { l: 'EPSS', v: f.ep ? `${Math.round(f.ep * 100)}%` : '-', c: C.t1 },
        { l: 'Target', v: targetLabel(f) || affected.hosts[0] || affected.paths[0] || '-', c: C.t1 },
        { l: 'Module', v: moduleName, c: C.t1 },
      ];
  const lifecycleActions = (() => {
    if (!canWrite) return [];
    if (isSuppressed) {
      return [
        {
          label: restoring ? 'Restoring...' : 'Restore',
          detail: 'Returns this finding to normal portal views.',
          icon: RotateCcw,
          disabled: restoring,
          variant: 'sec',
          style: { color: C.sky },
          onClick: handleRestoreFinding,
        },
      ];
    }

    const actions = [];
    if (isOpen) {
      actions.push({
        label: 'Claim Remediation',
        detail: 'Moves ownership into remediation claimed.',
        icon: UserCheck,
        variant: 'sec',
        style: { color: C.amb },
        onClick: () => openModal(FINDING_STATUS.REMEDIATION_CLAIMED),
      });
    }
    if (canMarkResolved) {
      actions.push({
        label: 'Mark Resolved / Verified',
        detail: 'Close after validation evidence is ready.',
        icon: CheckCircle2,
        variant: 'pri',
        style: { background: C.grn },
        onClick: () => openModal(FINDING_STATUS.VERIFIED),
      });
    }
    if (isOpen || f.status === 'remediation_claimed') {
      actions.push({
        label: 'Accept Risk',
        detail: 'Requires justification; suppresses re-detection for 365 days.',
        icon: ShieldAlert,
        variant: 'sec',
        style: { color: C.amb },
        onClick: () => openModal(FINDING_STATUS.ACCEPTED_RISK),
      });
    }
    if (!isOpen && f.status !== 'reopened') {
      actions.push({
        label: 'Reopen Finding',
        detail: 'Returns the lifecycle to reopened.',
        icon: RotateCcw,
        variant: 'sec',
        onClick: () => openModal(FINDING_STATUS.REOPENED),
      });
    }
    actions.push({
      label: 'Hide / Suppress',
      detail: 'Removes it from normal portal views.',
      icon: EyeOff,
      variant: 'ghost',
      style: { color: C.red },
      onClick: () => { setHideReason(''); setHideError(''); setShowHideModal(true); },
    });
    return actions;
  })();

  return (
    <Shell title={displayText(f.title, 'Finding')} sub={`${familyGuidance.label} finding / ${isExecutive ? 'Executive view' : 'Technical view'}`}>
      {/* Copy ID toast */}
      {idCopied && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '8px 16px', borderRadius: 6, background: `${C.grn}12`, border: `1px solid ${C.grn}33`, color: C.grn, fontSize: 11, fontFamily: FONTS.mono }}>
          Finding ID copied
        </div>
      )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '8px 16px', borderRadius: 6, background: `${C.sky}12`, border: `1px solid ${C.sky}33`, color: C.sky, fontSize: 11, fontFamily: FONTS.mono }}>
          {copyToast}
        </div>
      )}
      {/* Status modal overlay */}
      {showStatusModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowStatusModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>
              {STATUS_ACTION_COPY[targetStatus]?.title || 'Update Finding'}
            </div>
            <div style={{ fontSize: 12, color: C.t2, marginBottom: 12, lineHeight: 1.5 }}>
              {STATUS_ACTION_COPY[targetStatus]?.prompt || 'Add an optional note for this status change:'}
            </div>
            <textarea
              value={statusNote}
              onChange={e => setStatusNote(e.target.value)}
              placeholder={STATUS_ACTION_COPY[targetStatus]?.placeholder || 'Optional note...'}
              rows={4}
              style={{
                width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
                padding: '9px 11px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono,
                outline: 'none', resize: 'vertical', lineHeight: 1.6, marginBottom: 14,
                boxSizing: 'border-box',
              }}
            />
            {statusError && (
              <div style={{ fontSize: 11, color: C.red, fontFamily: FONTS.mono, marginBottom: 10, padding: '6px 10px', background: C.redBg, borderRadius: 4 }}>
                {statusError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="sec" onClick={() => setShowStatusModal(false)}>Cancel</Button>
              <Button
                variant="pri"
                disabled={updatingStatus || (targetStatus === FINDING_STATUS.ACCEPTED_RISK && !statusNote.trim())}
                onClick={() => handleStatusUpdate(targetStatus)}
              >
                {updatingStatus ? 'Updating...' : STATUS_ACTION_COPY[targetStatus]?.confirm || 'Update'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hide finding modal */}
      {showHideModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowHideModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>Hide this finding from the portal?</div>
            <div style={{ fontSize: 12, color: C.t2, marginBottom: 12, lineHeight: 1.5 }}>
              Provide a reason for hiding this finding:
            </div>
            <textarea
              value={hideReason}
              onChange={e => setHideReason(e.target.value)}
              placeholder="e.g. False positive \u2014 this IP is a known test system"
              rows={3}
              style={{
                width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
                padding: '9px 11px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono,
                outline: 'none', resize: 'vertical', lineHeight: 1.6, marginBottom: 10,
                boxSizing: 'border-box',
              }}
            />
            <div style={{
              padding: '8px 12px', borderRadius: 6, background: `${C.amb}10`, border: `1px solid ${C.amb}33`,
              fontSize: 10, color: C.amb, fontFamily: FONTS.mono, lineHeight: 1.5, marginBottom: 14,
            }}>
              This finding will be hidden from all views. The underlying data is preserved.
            </div>
            {hideError && (
              <div style={{ fontSize: 11, color: C.red, fontFamily: FONTS.mono, marginBottom: 10, padding: '6px 10px', background: C.redBg, borderRadius: 4 }}>
                {hideError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="sec" onClick={() => setShowHideModal(false)}>Cancel</Button>
              <Button variant="pri" style={{ background: C.red }} disabled={hiding || !hideReason.trim()} onClick={handleHideFinding}>
                {hiding ? 'Hiding...' : 'Hide Finding'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Credential validation modal */}
      {showValidateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !validating && setShowValidateModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>
              Validate Credential
            </div>
            <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.6, marginBottom: 16 }}>
              This will attempt to authenticate with this credential against <strong style={{ color: C.t1 }}>{validateProvider}</strong>. This action is logged and requires client authorization. Proceed?
            </div>
            {validating ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0' }}>
                <div style={{ width: 16, height: 16, border: `2px solid ${C.sky}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>Validating credential...</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="sec" onClick={() => setShowValidateModal(false)}>Cancel</Button>
                <Button variant="pri" onClick={handleValidateCredential}>Validate</Button>
              </div>
            )}
          </div>
        </div>
      )}

      <Breadcrumb items={breadcrumbItems} />
      <div style={{ marginBottom: 12 }}><Button variant="ghost" onClick={goBackToSource}>{'\u2190'} Back to {backTarget.label}</Button></div>
      {canCorrectFindings(profile) && clientId && id && (
        <FindingCorrectionEditor key={`${clientId}:${id}`} api={api} clientId={clientId} findingId={id}
          onSaved={() => { setLiveSev(null); refetchAllFindings(); refetchFinding(); refetchStatusHistory(); }} />
      )}
      <Card style={{ padding: 24, marginBottom: 16, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(220px, 280px)', gap: 18, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <h1 style={{ margin: 0, color: C.t1, fontFamily: FONTS.sans, fontSize: 22, lineHeight: 1.25, letterSpacing: 0 }}>{displayText(f.title, 'Finding')}</h1>
              <button onClick={copyFindingId} title="Copy finding ID" style={{ background: 'none', border: `1px solid ${C.b1}`, borderRadius: 5, padding: '4px 7px', cursor: 'pointer', fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                {displayText(f.id || id)}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
              <SeverityBadge value={liveSev ?? f.sev} />
              <StatusBadge value={f.status} />
              <PassiveEvidenceBadges finding={f} />
              {f.kev && f.cve && <KevBadge />}
            </div>
            {topCompliancePills.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
                <span style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, alignSelf: 'center' }}>Supports</span>
                {topCompliancePills.map(label => <Tag key={label} label={label} color={C.sky} />)}
                {Array.isArray(f.complianceMappings) && f.complianceMappings.length > topCompliancePills.length && (
                  <button onClick={() => setShowAllCompliance(true)} style={{ background: 'none', border: 'none', color: C.sky, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono, padding: 0 }}>
                    +{f.complianceMappings.length - topCompliancePills.length} more
                  </button>
                )}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
              {heroMetrics.map(metric => (
                <div key={metric.l}>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>{metric.l}</div>
                  <div style={{ color: metric.c, fontSize: isExecutive ? 13 : 15, fontWeight: 900, fontFamily: isExecutive ? FONTS.sans : FONTS.mono, wordBreak: 'break-word', lineHeight: 1.35 }}>{displayText(metric.v, '-')}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: C.t1, fontFamily: FONTS.sans }}>Lifecycle</div>
              <StatusBadge value={f.status} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 4, marginBottom: 12 }}>
              {LIFECYCLE_STEPS.map(step => {
                const active = (step.key === 'hidden' && isSuppressed) || step.states.includes(f.status);
                return (
                  <div key={step.key} title={step.label} style={{
                    minWidth: 0,
                    height: 6,
                    borderRadius: 3,
                    background: active ? step.color : C.b1,
                    opacity: active ? 1 : 0.55,
                  }} />
                );
              })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {LIFECYCLE_STEPS.map(step => {
                const active = (step.key === 'hidden' && isSuppressed) || step.states.includes(f.status);
                return (
                  <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? step.color : C.b1, flexShrink: 0 }} />
                    <span style={{ color: active ? C.t1 : C.t2, fontSize: 10, fontFamily: FONTS.mono, fontWeight: active ? 800 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
            {!canWrite ? (
              <div style={{ padding: '9px 10px', borderRadius: 6, background: C.s1, border: `1px solid ${C.b1}`, color: C.t2, fontSize: 10, lineHeight: 1.45 }}>
                Client users can view lifecycle state but cannot change finding disposition.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lifecycleActions.map(action => {
                  const Icon = action.icon || ShieldCheck;
                  return (
                    <Button
                      key={action.label}
                      variant={action.variant}
                      disabled={updatingStatus || restoring || action.disabled}
                      title={action.title}
                      style={{ width: '100%', minHeight: 38, justifyContent: 'flex-start', textAlign: 'left', ...action.style }}
                      onClick={action.onClick}
                    >
                      <Icon size={14} />
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 800 }}>{action.label}</span>
                        <span style={{ fontSize: 9, color: action.variant === 'pri' ? 'rgba(255,255,255,0.78)' : C.t2, fontFamily: FONTS.mono, whiteSpace: 'normal', lineHeight: 1.25 }}>{action.detail}</span>
                      </span>
                    </Button>
                  );
                })}
                {restoreError && (
                  <div style={{ fontSize: 10, color: C.red, fontFamily: FONTS.mono, lineHeight: 1.45 }}>
                    {restoreError}
                  </div>
                )}
                {psaConfigured ? (
                  <Button variant="ghost" style={{ width: '100%', fontSize: 10, color: C.sky }} disabled={creatingTicket} onClick={handleCreateTicket}>
                    {creatingTicket ? 'Creating ticket...' : 'Create PSA Ticket'}
                  </Button>
                ) : psaConfigured === false ? (
                  <Button variant="ghost" style={{ width: '100%', fontSize: 10, color: C.sky }} onClick={() => navigate('/msp/settings')}>
                    Configure PSA Integration {'\u2192'}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </Card>

      {ticketResult && (
        <div style={{ padding: '10px 12px', borderRadius: 7, background: C.s1, border: `1px solid ${C.grn}33`, marginBottom: 14, fontSize: 11, fontFamily: FONTS.mono }}>
          <span style={{ color: C.grn, fontWeight: 800 }}>Ticket created</span>
          {ticketResult.externalTicketUrl ? (
            <a href={ticketResult.externalTicketUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: C.sky, textDecoration: 'none' }}>{ticketResult.externalTicketId} {'\u2197'}</a>
          ) : ticketResult.externalTicketId ? (
            <span style={{ color: C.t2, marginLeft: 8 }}>ID: {ticketResult.externalTicketId}</span>
          ) : null}
        </div>
      )}
      {ticketError && <div style={{ fontSize: 10, color: C.red, fontFamily: FONTS.mono, marginBottom: 14 }}>{ticketError}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 22 }}>
            {/* Validation result banner */}
            {validateResult && (() => {
              const r = validateResult;
              const RESULT_STYLES = {
                active:        { bg: C.redBg, border: C.red, color: C.red, text: 'Active credential confirmed \u2014 rotate immediately' },
                mfa_blocked:   { bg: C.redBg, border: C.red, color: C.red, text: 'Password valid \u2014 blocked by MFA/Conditional Access' },
                not_in_use:    { bg: C.gryBg, border: C.gry, color: C.gry, text: `Password not in use as of ${new Date().toLocaleDateString()}` },
                not_found:     { bg: C.gryBg, border: C.gry, color: C.gry, text: 'Account not found in tenant' },
                disabled:      { bg: C.ambBg, border: C.amb, color: C.amb, text: 'Account disabled or locked' },
              };
              const s = RESULT_STYLES[r.result] ?? { bg: C.orgBg, border: C.org, color: C.org, text: `Validation inconclusive \u2014 ${r.message || r.error || 'unknown error'}` };
              return (
                <div style={{ padding: '12px 14px', borderRadius: 7, background: s.bg, border: `1px solid ${s.border}33`, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: s.color, fontFamily: FONTS.sans }}>{s.text}</div>
                  {r.validatedAt && <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 4 }}>Validated {new Date(r.validatedAt).toLocaleString()}</div>}
                </div>
              );
            })()}
            {validateError && (
              <div style={{ padding: '12px 14px', borderRadius: 7, background: C.orgBg, border: `1px solid ${C.org}33`, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.org, fontFamily: FONTS.sans }}>Validation inconclusive &mdash; {validateError}</div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 0, background: C.s2, borderRadius: 7, border: `1px solid ${C.b1}`, overflow: 'hidden', marginBottom: 18 }}>
              {[
                { l: 'Detected', v: age ? exactTime(age) : '\u2014' },
                { l: 'Owner', v: recommendedOwner },
                { l: 'SLA', v: remediationSla },
                { l: 'Effort', v: effort },
              ].map((r, i) => (
                <div key={r.l} style={{ padding: '14px 16px', borderRight: i === 3 ? 'none' : `1px solid ${C.b1}` }}>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>{r.l}</div>
                  <div style={{ fontSize: 12, color: C.t1, fontFamily: FONTS.sans, fontWeight: 900, lineHeight: 1.4, wordBreak: 'break-word' }}>{displayText(r.v)}</div>
                </div>
              ))}
            </div>
            {!isExecutive && <SddAccessDetail finding={f} />}
            {/* Breach credential detail section */}
            {f.additionalData && /^breach-/i.test(f.module) && (() => {
              const cred = f.additionalData;
              const isPlaintext = cred.credentialType === 'plaintext';
              const copyHash = () => {
                navigator.clipboard.writeText(cred.hashValue || '');
                setHashCopied(true);
                setTimeout(() => setHashCopied(false), 2000);
              };
              const openValidate = (provider) => {
                setValidateProvider(provider);
                setValidateError('');
                setValidateResult(null);
                setShowValidateModal(true);
              };
              const hasProvider = m365 || adEnabled;
              return (
                <div style={{ marginBottom: 18, padding: 14, background: C.s2, borderRadius: 7, border: `1px solid ${C.b1}` }}>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 10 }}>Breached Credential</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {cred.email && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Email</span>
                        <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, fontWeight: 600 }}>{displayText(cred.email)}</span>
                      </div>
                    )}
                    {isPlaintext && cred.plaintextPassword && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Password</span>
                          <span style={{ fontSize: 8, color: C.red, fontFamily: FONTS.mono, fontWeight: 700, textTransform: 'uppercase', padding: '1px 5px', background: C.redBg, borderRadius: 3 }}>Plaintext — Rotate immediately</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, fontWeight: 600, letterSpacing: showPwd ? 0 : '0.1em' }}>
                            {showPwd ? displayText(cred.plaintextPassword) : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                          </span>
                          <button onClick={() => setShowPwd(p => !p)} title={showPwd ? 'Hide' : 'Reveal'} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: C.t2, padding: '0 2px' }}>
                            {showPwd ? '\u{1F441}' : '\u{1F441}\u200D\u{1F5E8}'}
                          </button>
                        </div>
                      </div>
                    )}
                    {!isPlaintext && cred.hashValue && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Hash</span>
                          {cred.hashType && <span style={{ fontSize: 8, color: C.amb, fontFamily: FONTS.mono, fontWeight: 700, padding: '1px 5px', background: C.ambBg, borderRadius: 3 }}>{displayText(cred.hashType)}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{displayText(cred.hashValue).length > 20 ? displayText(cred.hashValue).slice(0, 20) + '\u2026' : displayText(cred.hashValue)}</span>
                          <button onClick={copyHash} title="Copy hash" style={{ background: 'none', border: `1px solid ${C.b1}`, borderRadius: 3, cursor: 'pointer', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, padding: '1px 5px' }}>
                            {hashCopied ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      </div>
                    )}
                    {cred.breachSource && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Breach Source</span>
                        <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{displayText(cred.breachSource)}</span>
                      </div>
                    )}
                  </div>
                  {/* Validate credential button — plaintext only */}
                  {isPlaintext && (
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.b1}` }}>
                      {!hasProvider && !modulesLoading ? (
                        <div title="Configure M365 or AD integration to enable validation" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: FONTS.sans, background: C.s2, color: C.t3, border: `1px solid ${C.b1}`, cursor: 'not-allowed', opacity: 0.5 }}>
                            Validate Credential
                          </button>
                          <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>Configure M365 or AD integration to enable</span>
                        </div>
                      ) : m365 && adEnabled ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button variant="pri" style={{ fontSize: 11 }} onClick={() => openValidate('M365')}>Validate Against M365</Button>
                          <Button variant="pri" style={{ fontSize: 11, background: C.sky }} onClick={() => openValidate('AD')}>Validate Against AD</Button>
                        </div>
                      ) : m365 ? (
                        <Button variant="pri" style={{ fontSize: 11 }} onClick={() => openValidate('M365')}>Validate Against M365</Button>
                      ) : adEnabled ? (
                        <Button variant="pri" style={{ fontSize: 11, background: C.sky }} onClick={() => openValidate('AD')}>Validate Against AD</Button>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })()}
            {!isExecutive && f.cve && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>CVE Reference</div>
                <a href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(displayText(f.cve, ''))}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <Tag label={displayText(f.cve)} color={C.amb} />
                </a>
              </div>
            )}
            {(() => {
              const cwe = cweIdOf(f);
              if (isExecutive) return null;
              if (!cwe) return null;
              const cweLabel = String(cwe).startsWith('CWE-') ? cwe : `CWE-${cwe}`;
              return (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>CWE Reference</div>
                  <a href={`https://cwe.mitre.org/data/definitions/${String(cwe).replace(/\D/g, '')}.html`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Tag label={cweLabel} color={C.pur} />
                  </a>
                </div>
              );
            })()}
            {!isExecutive && f.mitre && f.mitre.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>MITRE ATT&CK Techniques</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{f.mitre.map(t => {
                  const technique = displayText(t, '');
                  const mitreId = technique.replace(/\s.*/,'').replace(/\./g,'/');
                  return (
                    <a key={`${technique}-${mitreId}`} href={`https://attack.mitre.org/techniques/${mitreId}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <Tag label={technique} color={C.pur} />
                    </a>
                  );
                })}</div>
              </div>
            )}
            <div style={{ marginBottom: 18, background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {(f.cve || cwe) && (
                  <span style={{ flexShrink: 0, color: C.sky, background: `${C.sky}12`, border: `1px solid ${C.sky}22`, borderRadius: 5, padding: '5px 8px', fontSize: 11, fontFamily: FONTS.mono, fontWeight: 800 }}>
                    {displayText(f.cve || cwe)}
                  </span>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <SectionLabel color={C.sky}>{plainEnglish ? 'Why this matters in plain English' : 'Technical Description'}</SectionLabel>
                    <button onClick={() => setPlainEnglish(v => !v)} style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 999, color: C.sky, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.sans, padding: '4px 9px' }}>
                      {plainEnglish ? 'Show technical' : 'Plain English'}
                    </button>
                  </div>
                  <TextBlock>{plainEnglish ? `${businessImpact} ${actionSummary}` : displayText(f.description, 'No description is available. Review the affected asset, source module, and remediation plan before assigning closure.')}</TextBlock>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 18, background: `${C.sky}08`, border: `1px solid ${C.sky}22`, borderLeft: `4px solid ${C.sky}`, borderRadius: 8, padding: 16 }}>
              <SectionLabel color={C.sky}>Remediation</SectionLabel>
              <StepList steps={remediationSteps} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 18 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 7, background: `${C.grn}18`, color: C.grn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>B</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.grn, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, fontWeight: 900 }}>Business & Client</div>
                    <div style={{ fontSize: 10, color: C.t2, marginTop: 2 }}>Client-ready narrative for QBRs, approvals, and stakeholder email.</div>
                  </div>
                  <Button variant="sec" style={{ fontSize: 10 }} onClick={() => copyToClipboard(clientEmailText, () => showCopied('Client email copied'))}>
                    Copy as client email
                  </Button>
                </div>
                <InsightCard title="Impact Summary" accent={C.grn}>
                  <TextBlock>{businessImpact}</TextBlock>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 14 }}>
                    <MetricTile label="Impact Band" value={impactBand} />
                    <MetricTile label="Remediation Leverage" value={remediationLeverage} highlight={C.grn} />
                  </div>
                </InsightCard>
                <InsightCard title="Action Summary" accent={C.grn}>
                  <TextBlock>{actionSummary}</TextBlock>
                </InsightCard>
                <InsightCard title="Client Conversation" accent={C.amb}>
                  <div style={{ background: `${C.amb}10`, border: `1px solid ${C.amb}33`, borderRadius: 7, padding: 14 }}>
                    <div style={{ color: C.amb, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, fontWeight: 900, marginBottom: 7 }}>Talk Track</div>
                    <TextBlock>{clientConversation}</TextBlock>
                  </div>
                </InsightCard>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 7, background: `${C.sky}18`, color: C.sky, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>E</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.sky, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, fontWeight: 900 }}>Engineer & Validation</div>
                    <div style={{ fontSize: 10, color: C.t2, marginTop: 2 }}>Technician-ready remediation package for PSA tickets and validation.</div>
                  </div>
                  <Button variant="sec" style={{ fontSize: 10 }} onClick={() => copyToClipboard(ticketText, () => showCopied('Ticket block copied'))}>
                    Copy as ticket
                  </Button>
                </div>
                <InsightCard title="Validation Steps" accent={C.sky}>
                  <StepList steps={validationSteps} />
                </InsightCard>
                <InsightCard title="Evidence Checklist" accent={C.sky}>
                  <TextBlock>{evidenceChecklist}</TextBlock>
                </InsightCard>
                <InsightCard title="Exit Criteria" accent={C.sky}>
                  <TextBlock>{exitCriteria}</TextBlock>
                </InsightCard>
                <InsightCard title="Technical Context" accent={C.pur}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                    <MetricTile label="Source Module" value={moduleName} />
                    <MetricTile label="Service / Target" value={targetLabel(f) || affected.hosts[0] || affected.paths[0] || '\u2014'} />
                  </div>
                  {technicalContext && <div style={{ marginTop: 12 }}><TextBlock>{technicalContext}</TextBlock></div>}
                </InsightCard>
              </div>
            </div>
            {!isExecutive && f.complianceMappings && f.complianceMappings.length > 0 && (() => {
              const controlLabel = (m) => {
                if (typeof m === 'string') return m;
                if (!m || typeof m !== 'object') return '';
                const id = m.controlId ?? m.control ?? '';
                const name = m.controlName ?? '';
                if (id && name) return `${displayText(id, '')} \u2014 ${displayText(name, '')}`;
                return displayText(id, '');
              };
              const frameworkOf = (m) => {
                if (typeof m === 'string') return 'Other';
                return frameworkDisplayName(m?.frameworkName ?? m?.frameworkId ?? m?.framework ?? 'Other');
              };

              // Group by framework
              const groups = new Map();
              for (const m of f.complianceMappings) {
                const fw = frameworkOf(m);
                const lbl = controlLabel(m);
                if (!lbl) continue;
                if (!groups.has(fw)) groups.set(fw, []);
                groups.get(fw).push(lbl);
              }

              const totalCount = f.complianceMappings.length;
              // When collapsed, show up to 5 controls total across groups (preserving group order)
              const LIMIT = 5;
              let remaining = LIMIT;
              const limitedGroups = [];
              for (const [fw, ctrls] of groups) {
                if (showAllCompliance) {
                  limitedGroups.push([fw, ctrls]);
                } else if (remaining > 0) {
                  const slice = ctrls.slice(0, remaining);
                  limitedGroups.push([fw, slice]);
                  remaining -= slice.length;
                }
              }

              return (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 7 }}>
                    Compliance Mappings <span style={{ color: C.t3 }}>({totalCount})</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {limitedGroups.map(([fw, ctrls]) => (
                      <div key={fw}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{displayText(fw)}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {ctrls.map((lbl, i) => {
                            const href = complianceControlUrl(fw, lbl);
                            const tag = <Tag label={displayText(lbl)} color={C.sky} />;
                            return href ? (
                              <a key={i} href={href} target="_blank" rel="noopener noreferrer" title="Open NIST RMF control reference" style={{ textDecoration: 'none' }}>
                                {tag}
                              </a>
                            ) : <span key={i}>{tag}</span>;
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalCount > LIMIT && (
                    <button
                      onClick={() => setShowAllCompliance(v => !v)}
                      style={{ background: 'none', border: 'none', color: C.sky, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono, padding: 0, marginTop: 8 }}
                    >
                      {showAllCompliance ? 'Show less' : `Show all ${totalCount} compliance mappings`}
                    </button>
                  )}
                </div>
              );
            })()}
            {!isExecutive && (() => {
              const { paths, hosts, total } = affectedLocations(f);
              if (total === 0) return null;
              return <AffectedLocations paths={paths} hosts={hosts} total={total} />;
            })()}
            {!isExecutive && duplicateGroupKey && (
              <DuplicateGroupSection
                groupKey={duplicateGroupKey}
                group={duplicateGroup}
                loading={duplicateGroupLoading}
                error={duplicateGroupError}
                currentFindingId={f.id}
                onOpen={openDuplicateMember}
              />
            )}
            {!isExecutive && (() => {
              const refs = referenceListOf(f);
              if (refs.length === 0) return null;
              return (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 7 }}>References</div>
                  <div style={{ display: 'grid', gap: 7 }}>
                    {refs.map((ref, i) => (
                      <a key={`${ref.url}-${i}`} href={ref.url} target="_blank" rel="noopener noreferrer" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                        padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.b1}`, background: C.s2,
                        color: C.sky, textDecoration: 'none', fontSize: 11, fontFamily: FONTS.mono,
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText(ref.title || ref.url)}</span>
                        <span style={{ color: C.t2, flexShrink: 0 }}>Open</span>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}
            {!isExecutive && (f.detail?.evidence || f.detail?.additionalContext) && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 7 }}>Evidence</div>
                {f.detail.evidence && (
                  <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 7, padding: 14, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.7, fontFamily: FONTS.mono, whiteSpace: 'pre-wrap' }}>{displayText(f.detail.evidence)}</div>
                  </div>
                )}
                {f.detail.additionalContext && (() => {
                  let parsed = null;
                  if (typeof f.detail.additionalContext === 'string') {
                    try { parsed = JSON.parse(f.detail.additionalContext); } catch { /* not JSON */ }
                  } else if (typeof f.detail.additionalContext === 'object') {
                    parsed = f.detail.additionalContext;
                  }
                  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    return (
                      <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 7, padding: 14 }}>
                        <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 7 }}>Additional Context</div>
                        {Object.entries(parsed).map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.b2}` }}>
                            <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{k}</span>
                            <span style={{ fontSize: 10, color: C.t1, fontFamily: FONTS.mono, maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{displayText(v)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  const text = typeof f.detail.additionalContext === 'string' ? f.detail.additionalContext : JSON.stringify(f.detail.additionalContext);
                  return (
                    <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 7, padding: 14 }}>
                      <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 7 }}>Additional Context</div>
                      <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.7, fontFamily: FONTS.mono, whiteSpace: 'pre-wrap' }}>{text}</div>
                    </div>
                  );
                })()}
              </div>
            )}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans }}>Activity Timeline</div>
              {statusHistoryLoading && <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>Loading history...</span>}
            </div>
            <div style={{ position: 'relative', paddingLeft: 18 }}>
              <div style={{ position: 'absolute', left: 6, top: 8, bottom: 8, width: 1, background: C.b1 }} />
              {timeline.map((t, i) => (
                <div key={t.key ?? i} style={{ position: 'relative', marginBottom: 14, paddingLeft: 14 }}>
                  <div style={{ position: 'absolute', left: -12, top: 4, width: 7, height: 7, borderRadius: '50%', background: t.dot, border: `2px solid ${C.s1}` }} />
                  <div style={{ fontSize: 12, color: C.t1, fontWeight: 500 }}>{t.l}</div>
                  {t.t && <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }} title={exactTime(t.t)}>{relativeTime(t.t)}</div>}
                  {t.note && <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.45, marginTop: 5 }}>{displayText(t.note)}</div>}
                </div>
              ))}
            </div>
            {statusHistoryError && (
              <div style={{ marginTop: 2, fontSize: 10, color: C.amb, fontFamily: FONTS.mono, lineHeight: 1.45 }}>
                Status history unavailable; showing current finding state.
              </div>
            )}
          </Card>
        </div>

      </div>
    </Shell>
  );
}
